import { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STORAGE_KEY = "ss_site_gate_ok";
const GATE_VERSION = "v1";
const GATE_PASSWORD = process.env.REACT_APP_SITE_PASSWORD || "";

export default function SiteGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => {
    if (typeof window === "undefined") return true;
    if (!GATE_PASSWORD) return true; // No password set → gate disabled
    return localStorage.getItem(STORAGE_KEY) === GATE_VERSION;
  });
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (unlocked) return;
    document.title = "Accés restringit · Shopping Salou";
  }, [unlocked]);

  const submit = (e) => {
    e.preventDefault();
    if (!GATE_PASSWORD) {
      setUnlocked(true);
      return;
    }
    if (value === GATE_PASSWORD) {
      localStorage.setItem(STORAGE_KEY, GATE_VERSION);
      setUnlocked(true);
      setError("");
    } else {
      setError("Contrasenya incorrecta");
      setValue("");
    }
  };

  if (unlocked) return children;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-primary/5 via-background to-accent/5" data-testid="site-gate">
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-border/60 bg-card/80 backdrop-blur-xl shadow-2xl p-8 sm:p-10">
          <div className="flex flex-col items-center text-center mb-6">
            <img
              src="/logo.jpg"
              alt="Shopping Salou"
              className="w-20 h-20 rounded-2xl object-cover shadow-lg mb-4"
              data-testid="site-gate-logo"
            />
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wide uppercase mb-3">
              <Lock className="w-3.5 h-3.5" aria-hidden="true" />
              Accés restringit
            </div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground leading-tight">
              Shopping Salou
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Aquest entorn de proves està protegit. Introdueix la contrasenya per continuar.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="site-gate-form">
            <div className="relative">
              <Input
                type={show ? "text" : "password"}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  if (error) setError("");
                }}
                placeholder="Contrasenya"
                autoFocus
                autoComplete="current-password"
                className="pr-12 h-12 text-base rounded-full"
                data-testid="site-gate-input"
                aria-invalid={!!error}
                aria-describedby={error ? "site-gate-error" : undefined}
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1.5"
                aria-label={show ? "Amaga contrasenya" : "Mostra contrasenya"}
                data-testid="site-gate-toggle"
                tabIndex={-1}
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {error && (
              <div
                id="site-gate-error"
                role="alert"
                className="text-sm text-destructive font-medium text-center"
                data-testid="site-gate-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full rounded-full h-12 text-base font-semibold"
              data-testid="site-gate-submit"
              disabled={!value.trim()}
            >
              <ShieldCheck className="w-4 h-4 mr-2" aria-hidden="true" />
              Accedir
            </Button>
          </form>

          <p className="text-[11px] text-muted-foreground text-center mt-6 leading-relaxed">
            Entorn de <span className="font-medium text-foreground">staging</span> · Ús exclusiu per a
            l'equip de Shopping Salou i partners autoritzats
          </p>
        </div>

        <div className="text-center mt-4 text-xs text-muted-foreground">
          © 2026 Shopping Salou · Associació de Comerciants
        </div>
      </div>
    </div>
  );
}
